import React, { useState, useEffect } from "react";
import { Divider, Avatar, Grid, Paper, Box, IconButton, Tooltip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import ExpandableTextField from './ExpandableTextField';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useApiManager from '../../../utils/Api/apiManager';
import ConfirmationDialog from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { format } from 'date-fns';
import NoteAttachment from "./NoteAttachment";
import { useParams } from 'react-router-dom';
import AccessTimeFilledIcon from '@mui/icons-material/AccessTimeFilled';
import Linkify from 'linkify-react';

const NoteItem = ({ note, fetchNotes, entity, users }) => {
    const [showIcons, setShowIcons] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const { del } = useApiManager();
    const params = useParams();
    // Assume the parameter is always the first one in the URL
    const paramValue = Object.values(params)[0];

    const handleOpenDialog = () => {
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
    };

    const handleDelete = async () => {
        const url = `/${entity}/${paramValue}/notes/${note.id}`;

        try {
            await del(url);
            fetchNotes();
        } catch (error) {
            console.error('Error deleting note:', error);
            // Handle error (e.g., show error message)
        } finally {
            handleCloseDialog(); // Close the dialog after deletion
        }
    }

    const handleEdit = () => {
        setIsEditing(true);
    };

    const handleSave = () => {
        setIsEditing(false);
        fetchNotes(); // Or whatever function you use to refresh the notes
    };

    const handleCancel = () => {
        setIsEditing(false);
    }

    const options = {
        target: '_blank',
        rel: 'noopener noreferrer',
    };

    return (
        <Box
            onMouseOver={() => setShowIcons(true)}
            onMouseOut={() => setShowIcons(false)}
            sx={{ position: 'relative' }}
        >
            <Grid container wrap="nowrap" spacing={2} sx={{ marginTop: 1 }}>
                <Grid item>
                    <Tooltip title={note?.createdByName}>
                        <Avatar src={note?.createdByProfilePicture} />
                    </Tooltip>
                </Grid>
                <Grid justifyContent="left" item xs zeroMinWidth>
                    {!isEditing ? (
                        <>
                            <h5 style={{ margin: 0, textAlign: "left" }}>{note.title}</h5>
                            <p style={{ textAlign: "left" }}><Linkify options={options}>{note?.content?.replace(/@\[(.+?)\]\((.+?)\)/g, '@$1')}</Linkify></p>
                            <p style={{ textAlign: "left", color: "gray" }}>
                                {note.createdAt ? (
                                    <>
                                        posted on {format(note.createdAt, 'MMMM do yyyy')}
                                        <Tooltip title={format(note.createdAt, 'MMMM do yyyy, h:mm:ss a')}>
                                            <IconButton size="small" sx={{ marginBottom: 0.5 }}>
                                                <AccessTimeFilledIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <>posted on —</>
                                )}
                                by {note.createdByName ?? 'Unknown'}
                            </p>

                            <NoteAttachment attachmentUri={note.uri} />
                        </>
                    ) : (
                        <ExpandableTextField
                            edit={true}
                            note={note}
                            fetchNotes={handleSave}
                            editHandleCancel={handleCancel}
                            entity={entity}
                            users={users}
                        />
                    )}
                </Grid>
            </Grid>
            {showIcons && !isEditing && (
                <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
                    <IconButton onClick={handleEdit} aria-label="edit" size="small">
                        <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={handleOpenDialog} aria-label="delete" size="small">
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
            <ConfirmationDialog
                open={openDialog}
                title="Confirm Action"
                content="Are you sure you want to delete this item?"
                onClose={handleCloseDialog}
                onConfirm={handleDelete}
                confirmText="Confirm"
                cancelText="Cancel"
            />
        </Box>
    );
};

const Notes = ({ data, fetchNotes, entity }) => {
    const { get } = useApiManager();
    const [users, setUsers] = useState([]);

    const fetchAndSetData = async () => {
        try {
            const data = await get('/users');
            const options = data.map(user => ({
                display: `${user.firstName} ${user.lastName}`,
                id: user.id,
            }));

            setUsers(options);
        } catch (error) {
            console.error('Error fetching data:', error);
        } 
    };

    useEffect(() => {
        fetchAndSetData();
    }, []);


    return (
        <>
            <Paper elevation={0} sx={{ p: 2 }}>
                <h4>Notes</h4>
                <Divider />
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                    {data.length > 0 ? (
                        data.map((note, index) => (
                            <div key={index}>
                                <NoteItem note={note} fetchNotes={fetchNotes} entity={entity} users={users} />
                                <Divider sx={{ marginBottom: 1 }} />
                            </div>
                        ))
                    ) : (
                        <p>No notes available</p>
                    )}
                </div>
                <ExpandableTextField fetchNotes={fetchNotes} entity={entity} users={users} />
            </Paper>
        </>
    );
};

export default Notes;
